import React, { useCallback } from 'react';

import { gql } from '@apollo/client';
import { EditableNameInput, EditableNameVariant } from 'src/components';
import { getIsNotFound } from 'src/components/not-found/get-is-not-found';
import { Apollo } from 'src/gql/generated';

void gql`
  query GetThreadTitle($id: UUID!) {
    agentThread(id: $id) {
      __typename
      id
      title
    }
  }
`;

interface ChatThreadTitleProps {
  /**
   * The panel's verdict on the thread. Its own query can't be trusted for
   * this: it is cache-first and selects only `id` and `title`, so a thread
   * still cached from the history list reads as present after the panel's
   * network query has found it gone.
   */
  isThreadNotFound: boolean;
  threadId: UUID;
}

export function ChatThreadTitle({ isThreadNotFound, threadId }: ChatThreadTitleProps) {
  const [updateThreadTitle] = Apollo.useUpdateAgentThreadTitleMutation();
  const { data, error, loading } = Apollo.useGetThreadTitleQuery({
    variables: { id: threadId },
  });
  const isUnavailable =
    isThreadNotFound || getIsNotFound({ entity: data?.agentThread, error, loading });
  const handleUpdateTitle = useCallback(
    async (newTitle: string) => {
      const trimmedTitle = newTitle.trim();
      await updateThreadTitle({
        optimisticResponse: {
          __typename: 'Mutation',
          updateAgentThreadTitle: {
            __typename: 'AgentThread',
            id: threadId,
            title: trimmedTitle,
          },
        },
        variables: {
          threadId,
          title: trimmedTitle,
        },
      });
    },
    [threadId, updateThreadTitle]
  );

  return (
    <EditableNameInput
      alignCenter
      disabled={isUnavailable}
      name={data?.agentThread?.title ?? ''}
      placeholder={isUnavailable ? '' : 'Enter a title...'}
      updateName={handleUpdateTitle}
      variant={EditableNameVariant.LargeText}
    />
  );
}
